import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, FormLabel, InputLabel, MenuItem,
  Radio, RadioGroup, Select, Typography,
} from '@mui/material'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'

interface ClassOption {
  class_id: string
  name:     string
}

interface Props {
  open:           boolean
  studentId:      string | null
  studentLabel:   string                  // e.g., "Zachary Li (zmli84@gmail.com)"
  fromState:      'trial_active' | 'trial_expired'
  onClose:        () => void
  onPromoted:     () => void               // parent re-fetches list after success
}

export default function PromoteDialog({
  open, studentId, studentLabel, fromState, onClose, onPromoted,
}: Props) {
  const [classes,      setClasses]      = useState<ClassOption[]>([])
  const [resetProject, setResetProject] = useState<'keep' | 'fresh'>('keep')
  const [classId,      setClassId]      = useState('')
  const [submitting,   setSubmitting]   = useState(false)
  const [error,        setError]        = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setResetProject('keep')
    setClassId('')
    setError(null)
    supabase
      .from('class')
      .select('class_id, name')
      .in('status', ['upcoming', 'active'])
      .order('name')
      .then(({ data }) => setClasses(data ?? []))
  }, [open])

  async function handlePromote() {
    if (!studentId) return
    setSubmitting(true)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('promote-to-enrolled', {
      body: {
        student_id:    studentId,
        reset_project: resetProject === 'fresh',
        class_id:      classId || undefined,
        reason:        `Promoted from ${fromState} (${resetProject === 'fresh' ? 'started fresh' : 'kept trial work'})`,
      },
    })
    if (invokeErr) {
      setError(await readFunctionError(invokeErr))
      setSubmitting(false); return
    }
    if (data?.error) {
      setError(data.error)
      setSubmitting(false); return
    }
    setSubmitting(false)
    onPromoted()
    onClose()
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Promote to Enrolled</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {studentLabel}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Current state: <strong>{fromState === 'trial_active' ? 'Trial — active' : 'Trial — expired'}</strong>
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl sx={{ mb: 3 }}>
          <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Project data</FormLabel>
          <RadioGroup
            value={resetProject}
            onChange={e => setResetProject(e.target.value as 'keep' | 'fresh')}
          >
            <FormControlLabel
              value="keep"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Keep their trial work</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Student continues from where they left off in the trial.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="fresh"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Start fresh</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Archive their trial project and create a clean new project.
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Class (optional)</InputLabel>
          <Select
            label="Class (optional)"
            value={classId}
            onChange={e => setClassId(e.target.value)}
          >
            <MenuItem value="">No class</MenuItem>
            {classes.map(c => (
              <MenuItem key={c.class_id} value={c.class_id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handlePromote}
          disabled={submitting || !studentId}
        >
          {submitting ? <CircularProgress size={20} /> : 'Promote → Enrolled'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
